/* eslint-disable */ // TODO: fix lint errors and remove this comment

import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import CallAiAssistantModal from '../CallAiAssistantModal';
import EmailAiAssistantModal from '../EmailAiAssistantModal';
import EmailTranscriptModal from '../EmailTranscriptModal';
import { createAiPayload, createAiStatusPayload } from '../../../common/utils';
import { useToken } from '../../../common/helper';
import { useSpin } from '../../../components/SpinContext';
import {
  initiateAiCall,
  initiateAiEmail,
  shipmentAlertAiStatus,
} from '../../../apis/shipment';
import { GetAiTransferNumber } from '../../../apis/settings';
import {
  ShipmentAlertData,
  SingleShipmentDataExtended,
} from '../../../apis/types';

interface ApiError {
  data?: {
    message?: string;
  };
  message?: string;
}

interface AIWorkFlowProps {
  selectedAlert: ShipmentAlertData | null;
  shipment: SingleShipmentDataExtended | null;
  showAiModal: boolean;
  setShowAiModal: (show: boolean) => void;
  aiTrigger: number;
  showEmailModal: boolean;
  setShowEmailAiModal: (show: boolean) => void;
  showEmailAiModal: boolean;
  setShowEmailModal: (show: boolean) => void;
  onAIFlowSuccess: (alert: ShipmentAlertData) => void;
}

/**
 * AI Workflow component for handling AI-powered call and email workflows for shipment alerts
 *
 * @param selectedAlert - The currently selected shipment alert
 * @param shipment - The shipment data
 * @param showAiModal - Controls visibility of AI call modal
 * @param setShowAiModal - Function to toggle AI call modal
 * @param aiTrigger - Trigger counter for automatic AI calls
 * @param showEmailModal - Controls visibility of email transcript modal
 * @param setShowEmailAiModal - Function to toggle email AI modal
 * @param showEmailAiModal - Controls visibility of email AI assistant modal
 * @param setShowEmailModal - Function to toggle email transcript modal
 * @param onAIFlowSuccess - Callback function when AI workflow succeeds
 */
const AIWorkFlow: React.FC<AIWorkFlowProps> = ({
  selectedAlert,
  shipment,
  showAiModal,
  setShowAiModal,
  aiTrigger,
  showEmailModal,
  setShowEmailAiModal,
  showEmailAiModal,
  setShowEmailModal,
  onAIFlowSuccess,
}) => {
  const { showSpin, hideSpin } = useSpin();
  const { token, getUserID, decodedUserToken, getToken } = useToken();
  const userID = getUserID();
  const decodedToken = decodedUserToken();
  const companyName = shipment?.company;
  const companyID = decodedToken?.company_id;
  const aiCompanyName = shipment?.ai_company_name;
  const [transferPhone, setTransferPhone] = useState<string | undefined>(
    undefined
  );

  useEffect(() => {
    GetAiTransferNumber(token)
      .then((res) => {
        const data = res?.data?.data?.data;
        if (data?.ai_transfer_number) {
          setTransferPhone(data.ai_transfer_number);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch AI transfer number:', error);
      });
  }, [token]);

  useEffect(() => {
    if (aiTrigger > 0) {
      handleAiCallConfirm(selectedAlert);
    }
  }, [aiTrigger]);

  const handleAiEmailConfirm = (
    alertData: ShipmentAlertData | null,
    emailMessage: string,
    emailSubject: string
  ) => {
    if (!alertData || !shipment) {
      console.warn('Shipment is not ready yet.');
      return;
    }
    const payload = createAiPayload({
      alertData,
      shipment,
      companyName,
      aiCompanyName,
      companyID,
      emailSubject,
      emailMessage,
      isEmailPayload: true,
      userID,
      includeSensitiveProducts: decodedToken?.include_sensitive_products,
    });
    const alert_id = alertData?.id;
    const aiStatusPayload = createAiStatusPayload({
      alertData,
      ai_status: 'ai_email_in_progress',
      id: alert_id,
    });
    showSpin();
    const t = getToken();
    initiateAiEmail(payload, t)
      .then((emailRes) => {
        if (emailRes?.status === 'success') {
          message.success('AI email sent successfully.');
          return shipmentAlertAiStatus(t, aiStatusPayload);
        } else {
          message.error(
            emailRes?.data?.data?.message || 'AI email sending failed'
          );
          throw new Error('AI email sending failed');
        }
      })
      .then((statusRes) => {
        if (statusRes?.data?.data?.is_success === true) {
          onAIFlowSuccess(alertData);
        }
      })
      .catch((error: ApiError) => {
        console.error('AI Email Error:', error);
        message.error(
          error?.data?.message || error.message || 'Something went wrong'
        );
      })
      .finally(() => {
        hideSpin();
      });
  };
  const handleAiCallConfirm = (alertData: ShipmentAlertData | null) => {
    if (!alertData || !shipment) {
      message.error('Shipment is not ready yet.');
      return;
    }
    const alert_id = alertData?.id;
    const aiCallPayload = createAiPayload({
      alertData,
      shipment,
      companyName,
      aiCompanyName,
      companyID,
      emailSubject: null,
      emailMessage: null,
      isEmailPayload: false,
      userID,
      transferPhone,
      includeSensitiveProducts: decodedToken?.include_sensitive_products,
    });
    const aiStatusPayload = createAiStatusPayload({
      alertData,
      ai_status: 'ai_call_in_progress',
      id: alert_id,
    });
    showSpin();
    const t = getToken();
    initiateAiCall(aiCallPayload, t)
      .then((callRes) => {
        if (callRes?.status === 'success') {
          message.success('AI call initiated successfully.');
          return shipmentAlertAiStatus(t, aiStatusPayload);
        } else {
          message.error(
            callRes?.data?.data?.message || 'AI call failed to initiate'
          );
        }
      })
      .then((statusRes) => {
        if (statusRes?.data?.data?.is_success === true) {
          onAIFlowSuccess(alertData);
        }
      })
      .catch((error: ApiError) => {
        void message.error(
          error?.data?.message || error.message || 'Something went wrong'
        );
      })
      .finally(() => {
        hideSpin();
      });
  };

  return (
    <>
      {showEmailAiModal && (
        <EmailAiAssistantModal
          setShowEmailModal={setShowEmailModal}
          open={showEmailAiModal}
          onCancel={() => setShowEmailAiModal(false)}
          onConfirm={() => {
            setShowEmailAiModal(false);
          }}
        />
      )}

      {showEmailModal && (
        <EmailTranscriptModal
          shipment={shipment}
          alert={selectedAlert}
          onConfirm={(message: string, subject: string) =>
            handleAiEmailConfirm(selectedAlert, message, subject)
          }
          open={showEmailModal}
          onClose={() => setShowEmailModal(false)}
        />
      )}
      {showAiModal && (
        <CallAiAssistantModal
          open={showAiModal}
          onCancel={() => setShowAiModal(false)}
          onConfirm={() => handleAiCallConfirm(selectedAlert)}
        />
      )}
    </>
  );
};
export default AIWorkFlow;
